import { Extension, Node, mergeAttributes } from "@tiptap/core";
import Suggestion from "@tiptap/suggestion";
import { PluginKey, Plugin } from "prosemirror-state";
import { emojisList, emojiGroupNames } from "@/Common/TextEditors/Helpers/emojis";
import { getClassName } from "./editorData";
import { generateAIHelper } from "./composables/aiHelper";
import { VueNodeViewRenderer } from "@tiptap/vue-3";
import AiNodeComp from "../Comp/AiComp/AiNode.vue";
import { TableCell } from "@tiptap/extension-table-cell"

import { Table } from '@tiptap/extension-table'

// Custom Image Node
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        customImage: {
            insertCustomImage: () => ReturnType
        }
    }
}
export const CustomImage = Node.create({
    name: "customImage",

    group: "block",
    atom: true, // acts as one unit (placeholder or image)

    addAttributes() {
        return {
            src: {
                default: null,
            },
        };
    },

    parseHTML() {
        return [{ tag: "custom-image" }];
    },

    renderHTML({ HTMLAttributes }) {
        // ✅ SSR / static render
        if (HTMLAttributes.src) {
            return ["img", mergeAttributes(HTMLAttributes)];
        }

        // ✅ placeholder in SSR
        return [
            "div",
            { class: "image-placeholder" },
            [
                "div",
                { class: "upload-box", contenteditable: "false" },
                "Click to upload or drag & drop",
            ],
        ];
    },

    addNodeView() {
        return ({ node, editor, getPos }) => {
            const dom = document.createElement("div");
            dom.classList.add("tiptap-editor-upload");

            // ✅ If image already exists, just show <img>
            if (node.attrs.src) {
                const img = document.createElement("img");
                img.src = node.attrs.src;
                dom.appendChild(img);
                return { dom };
            }

            // ✅ Otherwise, show uploader
            const uploader = document.createElement("div");
            uploader.classList.add("image-uploader");
            uploader.innerHTML = `
        <span class="tiptap-upload-icon"></span>
        <button class="upload-btn">Click to upload or Drag and Drop</button>
        <input type="file" accept="image/*" style="display:none;" />
      `;

            const fileInput = uploader.querySelector("input");
            const button = uploader.querySelector("button");

            button.addEventListener("click", () => fileInput.click());

            // Common handler for both input + drag-drop
            const handleFile = (file) => {
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => {
                    editor
                        .chain()
                        .focus()
                        .command(({ tr }) => {
                            tr.setNodeMarkup(getPos(), undefined, {
                                src: reader.result,
                            });
                            return true;
                        })
                        .run();
                };
                reader.readAsDataURL(file);
            };

            fileInput.addEventListener("change", (e) =>
                handleFile((e.target as HTMLInputElement).files?.[0])
            );

            // Drag & drop
            uploader.addEventListener("dragover", (e) => {
                e.preventDefault();
                uploader.classList.add("dragging");
            });

            uploader.addEventListener("dragleave", () => {
                uploader.classList.remove("dragging");
            });

            uploader.addEventListener("drop", (e) => {
                e.preventDefault();
                uploader.classList.remove("dragging");
                handleFile(e.dataTransfer.files[0]);
            });

            dom.appendChild(uploader);
            return { dom };
        };
    },

    addCommands() {
        return {
            insertCustomImage:
                () =>
                    ({ chain }) => {
                        return chain()
                            .insertContent({
                                type: this.name,
                                attrs: { src: null }, // placeholder first
                            })
                            .run();
                    },
        };
    },
});

/* ---------------------------------
   SLASH MENU EXTENSION
--------------------------------- */
export const SlashCommand = (slashCommands = []) =>
    Extension.create({
        name: "slashCommand",

        addProseMirrorPlugins() {
            return [
                Suggestion({
                    pluginKey: new PluginKey("slashCommand"),
                    editor: this.editor,
                    char: "/",
                    items: ({ query }) =>
                        slashCommands.filter((item) =>
                            item.title
                                .toLowerCase()
                                .startsWith(query.toLowerCase())
                        ),
                    render: () => {
                        let component;
                        // helpers
                        const updateList = (el, props) => {
                            el.innerHTML = "";

                            // no items → hide list
                            if (!props.items.length) {
                                el.style.display = "none";
                                return;
                            }

                            el.style.display = "block";

                            props.items.forEach((item) => {
                                const option = document.createElement("div");
                                option.classList.add("slash-item");
                                option.innerHTML = `<span class="icon ${item.iconClass}"></span> ${item.title}`;
                                option.addEventListener("click", () =>
                                    props.command(item)
                                );
                                el.appendChild(option);
                            });
                        };
                        const updatePosition = (el, props, container) => {
                            const rect = props.clientRect(); // selection rect
                            if (!rect) return;

                            const containerRect =
                                container.getBoundingClientRect();
                            const menuHeight = el.offsetHeight || 150; // fallback height
                            const spaceBelow =
                                containerRect.bottom - rect.bottom;
                            const spaceAbove = rect.top - containerRect.top;

                            let top;
                            if (
                                spaceBelow < menuHeight &&
                                spaceAbove > menuHeight
                            ) {
                                // Not enough space below, but enough above → place menu above
                                top = rect.top - containerRect.top - menuHeight;
                            } else {
                                // default below
                                top = rect.bottom - containerRect.top;
                            }

                            el.style.position = "absolute";
                            el.style.left =
                                rect.left - containerRect.left + 50 + "px";
                            el.style.top = top + "px";
                        };
                        return {
                            onStart: (props) => {
                                const editorWrapper =
                                    props.editor.view.dom.parentNode;
                                component = document.createElement("div");
                                component.classList.add("slash-menu");
                                editorWrapper.appendChild(component);

                                updateList(component, props);
                                updatePosition(component, props, editorWrapper);
                            },
                            onUpdate: (props) => {
                                updateList(component, props);
                                updatePosition(
                                    component,
                                    props,
                                    props.editor.view.dom.parentNode
                                );
                            },
                            onExit: () => {
                                component?.remove();
                                component = null;
                            },
                        };
                    },
                    command: ({ editor, range, props }) => {
                        props.command({ editor, range });
                    },
                }),
            ];
        },
    });

/* ---------------------------------
   MENTION EXTENSION
--------------------------------- */
export const MentionCommand = (mentions = []) =>
    Extension.create({
        name: "mentionCommand",

        addProseMirrorPlugins() {
            return [
                Suggestion({
                    pluginKey: new PluginKey("mentionCommand"),
                    editor: this.editor,
                    char: "@",
                    items: ({ query }) =>
                        mentions.filter((mention) =>
                            mention.mention_name
                                .toLowerCase()
                                .startsWith(query.toLowerCase())
                        ),
                    render: () => {
                        let component;
                        const updateList = (el, props) => {
                            el.innerHTML = "";

                            // no items → hide list
                            if (!props.items.length) {
                                el.style.display = "none";
                                return;
                            }

                            props.items.forEach((mention) => {
                                const option = document.createElement("div");
                                option.classList.add("mention-item");
                                const optionInnerHtml = mention?.mention_image
                                    ? `<span class="mention-image">
                                    <img src="${mention?.mention_image}">
                                    </span> ${mention?.mention_name}`
                                    : `<span class="mention-icon"><span class="${mention?.mention_icon}"></span></span> ${mention?.mention_name}`;
                                option.innerHTML = optionInnerHtml;
                                option.addEventListener("click", () =>
                                    props.command(mention)
                                );
                                el.appendChild(option);
                            });
                        };

                        const updatePosition = (el, props, container) => {
                            const rect = props.clientRect?.();
                            if (!rect) return;

                            const containerRect =
                                container.getBoundingClientRect();
                            const menuHeight = el.offsetHeight || 150;
                            const menuWidth = el.offsetWidth || 200;

                            const spaceBelow =
                                containerRect.bottom - rect.bottom;
                            const spaceAbove = rect.top - containerRect.top;

                            let y;
                            let maxHeight = menuHeight;
                         
                            // --- Placement logic ---
                            if (spaceBelow >= menuHeight) {
                                // ✅ enough space below
                                y = rect.bottom - containerRect.top;
                            } else if (
                                spaceBelow < menuHeight &&
                                spaceAbove >= menuHeight
                            ) {
                                // ✅ not enough below, but enough above
                                y = rect.top - containerRect.top - menuHeight;
                            } else if (spaceBelow >= spaceAbove) {
                                // ✅ more space below → clamp height
                                y = rect.bottom - containerRect.top;
                                maxHeight = spaceBelow;
                            } else {
                                // ✅ more space above → clamp height
                                y =
                                    rect.top -
                                    containerRect.top -
                                    Math.min(spaceAbove, menuHeight);
                                maxHeight = spaceAbove;
                            }

                            // --- X axis (prevent right overflow) ---
                            let x = rect.left - containerRect.left;
                            if (x + menuWidth > containerRect.width) {
                                x = containerRect.width - menuWidth - 8; // 8px padding
                            }

                            // --- Apply styles ---
                            el.style.position = "absolute";
                            el.style.top = "0";
                            el.style.left = "0";
                            el.style.transform = `translate(${x}px, ${y}px)`;
                            el.style.maxHeight = `${maxHeight}px`;
                            el.style.overflowY = "auto";

                            // Smooth transition for movement + height
                            el.style.transition =
                                "transform 150ms ease, max-height 150ms ease";
                        };

                        return {
                            onStart: (props) => {
                                const editorWrapper =
                                    props.editor.view.dom.parentNode;
                                component = document.createElement("div");
                                component.classList.add("mention-menu");
                                editorWrapper.appendChild(component);

                                updateList(component, props);
                                updatePosition(component, props, editorWrapper);
                            },
                            onUpdate: (props) => {
                                updateList(component, props);
                                updatePosition(
                                    component,
                                    props,
                                    props.editor.view.dom.parentNode
                                );
                            },
                            onExit: () => {
                                component?.remove();
                                component = null;
                            },
                        };
                    },
                    command: ({ editor, range, props }) => {
                        editor
                            .chain()
                            .focus()
                            .insertContentAt(range, [
                                {
                                    type: "mention",
                                    attrs: {
                                        id: props.id,
                                        label: props.mention_name,
                                        mentionSuggestionChar: "@",
                                        type: props?.mention_type,
                                        icon: props?.mention_icon,
                                        userid: props?.mention_user_id
                                    },
                                },
                                { type: "text", text: " " },
                            ])
                            .run();
                    },
                }),
            ];
        },
    });

export const Mention = Node.create({
    name: "mention",
    inline: true,
    group: "inline",
    atom: true,

    addAttributes() {
        return {
            id: {
                default: null,
            },
            label: {
                default: null,
            },
            icon: {
                default: null,
            },
            mentionSuggestionChar: {
                default: "@",
            },
            userid: {
                default: null
            },
            type: {
                default: null
            }
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-type="mention"]',
                getAttrs: (element) => ({
                    id: element.getAttribute("data-id"),
                    label: element.getAttribute("data-label"),
                    icon: element.getAttribute("data-icon"),
                    userid: element.getAttribute("data-userid"),
                    mentionSuggestionChar:
                        element.getAttribute("data-mention-suggestion-char") ||
                        "@",
                }),
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            "span",
            {
                class: "mention-tag",
                contenteditable: "false",
                "data-type": "mention",
                "data-id": HTMLAttributes.id,
                "data-label": HTMLAttributes.label,
                "data-icon": HTMLAttributes.icon,
                "data-userid": HTMLAttributes.userid,
                "data-mention-suggestion-char":
                    HTMLAttributes.mentionSuggestionChar || "@",
            },
            [
                "span",
                { class: "mention-tag-content" },
                [
                    "span",
                    { class: `mention-icon ${HTMLAttributes.icon || ""}` },
                ],
                [
                    "span",
                    { class: "mention-label" },
                    HTMLAttributes.label || "",
                ],
                [
                    "button",
                    {
                        class: "mention-delete",
                        type: "button",
                        onclick:
                            "this.closest('span[data-type=mention]').remove()",
                    },
                ],
            ],
        ];
    },
});

// Emoji Extension

export const emojiFromCode = (code) => {
    if (!code) return "❓"; // fallback if code is missing
    // remove dashes for multi-part codes
    const points = code.split("-").map((c) => parseInt(c, 16));
    return String.fromCodePoint(...points);
};

export const EmojiSuggestion = Extension.create({
    name: "emojiCommand",

    addProseMirrorPlugins() {
        return [
            Suggestion({
                pluginKey: new PluginKey("emojiCommand"),
                editor: this.editor,
                char: "#", // trigger
                items: () => {
                    const results = [];
                    Object.entries(emojisList).forEach(([group, emojis]) => {
                        emojis.forEach((emoji) => {
                            results.push({ ...emoji, group });
                        });
                    });
                    return results;
                },
                render: () => {
                    let component: any;
                    let activeGroup = null;

                    const updateList = (el, props) => {
                        if (!el) return;
                        const listWrapper = el.querySelector(
                            ".emoji-list-wrapper"
                        );
                        if (!listWrapper) return;
                        listWrapper.innerHTML = "";

                        // build unique groups with first emoji as icon
                        const seen = new Set();
                        const groups = props.items
                            .filter((e) => {
                                if (seen.has(e.group)) return false; // skip if group already added
                                seen.add(e.group);
                                return true;
                            })
                            .map((e) => ({
                                group: e.group,
                                icon: e.u, // now guaranteed first occurrence
                            }));

                        // groups container
                        const groupBar = document.createElement("div");
                        groupBar.classList.add("emoji-groups");

                        groups.forEach((g) => {
                            const btn = document.createElement("button");
                            btn.classList.add("emoji-group-btn");
                            btn.innerHTML = emojiFromCode(g.icon);
                            btn.title = emojiGroupNames[g.group];
                            btn.dataset.group = g.group;

                            // toggle active
                            if (!activeGroup) activeGroup = g.group; // default first group
                            if (g.group === activeGroup)
                                btn.classList.add("active");

                            btn.addEventListener("click", () => {
                                activeGroup = g.group;
                                updateList(el, props); // re-render with new active
                            });

                            groupBar.appendChild(btn);
                        });

                        listWrapper.appendChild(groupBar);

                        // emoji grid container
                        const emojiGroupHolder = document.createElement("div");
                        emojiGroupHolder.classList.add("emoji-group-holder");

                        const emojiGroupName = document.createElement("div");
                        emojiGroupName.classList.add("emoji-group-name");
                        emojiGroupName.innerHTML = emojiGroupNames[activeGroup];
                        emojiGroupHolder.appendChild(emojiGroupName);

                        const emojiGrid = document.createElement("div");
                        emojiGrid.classList.add("emoji-grid");
                        emojiGroupHolder.appendChild(emojiGrid);

                        listWrapper.appendChild(emojiGroupHolder);
                        props.items
                            .filter((emoji) => emoji.group === activeGroup)
                            .forEach((emoji) => {
                                const option = document.createElement("div");
                                option.classList.add("emoji-item");
                                option.textContent =
                                    emojiFromCode(emoji.u) || "❓";
                                option.addEventListener("click", () =>
                                    props.command(emoji)
                                );
                                emojiGrid.appendChild(option);
                            });

                        listWrapper.appendChild(emojiGroupHolder);
                    };

                    const updatePosition = (el, props, container) => {
                        if (!el) return;
                        const rect = props.clientRect?.();
                        if (!rect) return;

                        const containerRect = container.getBoundingClientRect();
                        const menuHeight = el.offsetHeight || 150;
                        const menuWidth = el.offsetWidth || 200;

                        const spaceBelow = containerRect.bottom - rect.bottom;
                        const spaceAbove = rect.top - containerRect.top;

                        let y;
                        let maxHeight = menuHeight;

                        if (spaceBelow >= menuHeight) {
                            y = rect.bottom - containerRect.top;
                        } else if (spaceAbove >= menuHeight) {
                            y = rect.top - containerRect.top - menuHeight;
                        } else if (spaceBelow >= spaceAbove) {
                            y = rect.bottom - containerRect.top;
                            maxHeight = spaceBelow;
                        } else {
                            y =
                                rect.top -
                                containerRect.top -
                                Math.min(spaceAbove, menuHeight);
                            maxHeight = spaceAbove;
                        }

                        let x = rect.left - containerRect.left;
                        if (x + menuWidth > containerRect.width) {
                            x = containerRect.width - menuWidth - 8;
                        }

                        el.style.position = "absolute";
                        el.style.top = "0";
                        el.style.left = "0";
                        el.style.transform = `translate(${x + 20}px, ${y}px)`;
                        el.style.maxHeight = `${maxHeight}px`;
                        el.style.overflowY = "auto";
                        el.style.transition =
                            "transform 150ms ease, max-height 150ms ease";
                    };

                    return {
                        onStart: (props) => {
                            const editorWrapper =
                                props.editor.view.dom.parentNode;

                            component = document.createElement("div");
                            component.classList.add("emoji-menu");

                            const menuIcons = document.createElement("div");
                            menuIcons.classList.add("emoji-list-wrapper");

                            component.appendChild(menuIcons);

                            editorWrapper.appendChild(component);

                            updateList(component, props);
                            updatePosition(component, props, editorWrapper);
                        },
                        onUpdate: (props) => {
                            updateList(component, props);
                            updatePosition(
                                component,
                                props,
                                props.editor.view.dom.parentNode
                            );
                        },
                        onExit: () => {
                            component?.remove();
                            component = null;
                        },
                    };
                },

                command: ({ editor, range, props }) => {
                    editor
                        .chain()
                        .focus()
                        .insertContentAt(range, [
                            {
                                type: "text",
                                text: emojiFromCode(props.u) || "❓",
                            },
                            { type: "text", text: " " },
                        ])
                        .run();
                },
            }),
        ];
    },
});

// Upload Image on Paste Extension
export const UploadImageOnPaste = Extension.create({
    name: "uploadImageOnPaste",

    addOptions() {
        return {
            uploadFn: (file) => {
                return Promise.resolve(URL.createObjectURL(file));
            },
            // Default allowed types (images)
            allowedFileTypes: [
                "image/png",
                "image/jpeg",
                "image/gif",
                "image/webp",
                "image/svg+xml",
            ],
            pasteImageType: "default",
        };
    },

    addProseMirrorPlugins() {
        const editor = this.editor;
        return [
            new Plugin({
                editor: this.editor,
                props: {
                    handlePaste: (view, event) => {
                        const clipboard = event.clipboardData;
                        const items = clipboard?.items || [];
                        const html = clipboard?.getData("text/html");
                        const text = clipboard?.getData("text/plain");

                        const pasteImageType = this.options.pasteImageType;

                        // 🧠 Case 1: Handle image paste
                        const imageItems = Array.from(items).filter((item) =>
                            this.options.allowedFileTypes.includes(item.type)
                        );

                        if (imageItems.length > 0 && pasteImageType === "pasteImageApi") {
                            event.preventDefault();
                            const files = [];
                            const base64Map = new Map();

                            const readers = imageItems.map(
                                (item) =>
                                    new Promise((resolve) => {
                                        const file = item.getAsFile();
                                        if (!file) return resolve(null);

                                        const reader = new FileReader();
                                        reader.onload = () => {
                                            const base64Src = reader.result;
                                            const { tr } = view.state;
                                            const node = view.state.schema.nodes.image.create({
                                                src: base64Src,
                                                "data-uploading": "true",
                                            });
                                            view.dispatch(tr.replaceSelectionWith(node));

                                            base64Map.set(base64Src, file);
                                            files.push(file);
                                            resolve(base64Src);
                                        };
                                        reader.readAsDataURL(file);
                                    })
                            );

                            Promise.all(readers).then(async () => {
                                try {
                                    const imagesData = await this.options.uploadFn(files);

                                    imagesData.images.forEach((image, i) => {
                                        const base64Src = Array.from(base64Map.keys())[i];
                                        let pos = -1;

                                        view.state.doc.descendants((node, posHere) => {
                                            if (
                                                node.type.name === "image" &&
                                                node.attrs.src === base64Src
                                            ) {
                                                pos = posHere;
                                                return false;
                                            }
                                            return true;
                                        });

                                        if (pos >= 0) {
                                            const trReplace = view.state.tr.setNodeMarkup(
                                                pos,
                                                undefined,
                                                {
                                                    ...(view.state.doc.nodeAt(pos)?.attrs || {}),
                                                    src: image?.[imagesData.imageKey],
                                                    "data-uploading": null,
                                                }
                                            );
                                            view.dispatch(trReplace);
                                        }
                                    });
                                } catch (err) {
                                    console.error("Upload failed", err);
                                }
                            });
                            return true; // ✅ stop default paste
                        }

                        if (html) {
                            event.preventDefault();
                            event.stopPropagation();

                            const cleanedHTML = minimalSanitizeHTML(html)

                            editor.commands.insertContent(cleanedHTML, {
                                parseOptions: {
                                    preserveWhitespace: "full"
                                }
                            });

                            return true;
                        }

                        // 🧾 Case 3: Plain text paste
                        if (text) {
                            event.preventDefault()
                            const formattedText = text
                                .replace(/\r\n/g, "\n")
                                .replace(/\n\n/g, "</p><p>")
                                .replace(/\n/g, "<br>")
                            editor.commands.insertContent(`<p>${formattedText}</p>`)
                            return true
                        }

                        return true; // ✅ Always block default TipTap paste
                    },
                },
            }),
        ];
    },
});


function minimalSanitizeHTML(html: any) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, "text/html")

    /* remove meta tags */
    doc.querySelectorAll("meta").forEach(el => el.remove())

    doc.querySelectorAll("*").forEach((el) => {

        /* remove unwanted attributes */
        el.removeAttribute("class")
        el.removeAttribute("id")
        el.removeAttribute("dir")

        /* remove docs internal attributes */
        Array.from(el.attributes).forEach(attr => {
            if (attr.name.startsWith("docs-")) {
                el.removeAttribute(attr.name)
            }
        })

        /* clean style */
        let style = el.getAttribute("style")

        if (style) {

            const cleaned = style
                .replace(/background-color\s*:\s*[^;]+;?/gi, "")
                .replace(/\bcolor\s*:\s*[^;]+;?/gi, "")
                .replace(/font-size\s*:\s*[^;]+;?/gi, "")
                .replace(/font-family\s*:\s*[^;]+;?/gi, "")
                .replace(/white-space\s*:\s*[^;]+;?/gi, "")

                /* fix broken css */
                .replace(/background-([a-z-]+)\s*:/gi, "$1:")

                .replace(/;;+/g, ";")
                .replace(/\s+/g, " ")
                .replace(/^\s*;\s*|\s*;\s*$/g, "")
                .trim()

            if (cleaned) {
                el.setAttribute("style", cleaned)
            } else {
                el.removeAttribute("style")
            }
        }

        /* remove Apple paste junk */
        if (el.classList?.contains("Apple-interchange-newline")) {
            el.remove()
        }

        /* remove empty span/div */
        if (
            (el.tagName === "SPAN" || el.tagName === "DIV") &&
            !el.textContent.trim() &&
            !el.children.length
        ) {
            el.remove()
        }

    })

    return doc.body.innerHTML
}

// Font Size Extension
export const FontSize = Extension.create({
    name: 'fontSize',

    addGlobalAttributes() {
        return [
            {
                types: ['textStyle'],
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: element => element.style.fontSize || null,
                        renderHTML: attributes => {
                            if (!attributes.fontSize) return {}
                            return { style: `font-size: ${attributes.fontSize}` }
                        },
                    },
                },
            },
        ]
    },

    addCommands() {
        return {
            setFontSize:
                fontSize =>
                    ({ chain }) => {
                        return chain().setMark('textStyle', { fontSize }).run()
                    },
            unsetFontSize:
                () =>
                    ({ chain }) => {
                        return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run()
                    },
        }
    },
})

// Font Family Extension
export const FontFamily = Extension.create({
    name: 'fontFamily',

    addGlobalAttributes() {
        return [
            {
                types: ['textStyle'],
                attributes: {
                    fontFamily: {
                        default: null,
                        parseHTML: element => {
                            // read inline style
                            return element.style.fontFamily || null
                        },
                        renderHTML: attributes => {
                            if (!attributes.fontFamily) return {}
                            // Add both inline style and class
                            const className = getClassName(attributes.fontFamily)
                            return {
                                style: `font-family: ${attributes.fontFamily}`,
                                class: className,
                            }
                        },
                    },
                },
            },
        ]
    },

    addCommands() {
        return {
            setFontFamily:
                fontFamily =>
                    ({ chain }) => {
                        return chain().setMark('textStyle', { fontFamily }).run()
                    },
            unsetFontFamily:
                () =>
                    ({ chain }) => {
                        return chain().setMark('textStyle', { fontFamily: null }).removeEmptyTextStyle().run()
                    },
        }
    },
})


// Ai Extension
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        aiText: {
            /**
             * Generate AI text from the current selection
             */
            generateAIText: (helperText: string) => ReturnType
        }
    }
}

export const AiNode = Node.create({
    name: "aiNode",
    group: "block",
    atom: true, // leaf node
    draggable: true,

    addAttributes() {
        return {
            content: { default: "" },
        };
    },

    parseHTML() {
        return [{ tag: "div[data-type='ai']" }];
    },

    renderHTML({ HTMLAttributes }) {
        // No 0 here! Leaf nodes cannot have children
        return ["div", { "data-type": "ai" }, HTMLAttributes.content || "Ask AI..."];
    },

    addNodeView() {
        return VueNodeViewRenderer(AiNodeComp);
    },
});

export const AiGenerateText = Extension.create({
    name: "aiText",

    addCommands() {
        return {
            generateAIText: (helperText: string) => ({ editor }) => {
                (async () => {
                    const { from, to } = editor.state.selection;
                    const selectedText = editor.state.doc.textBetween(from, to, "\n");
                    if (!selectedText) return false;

                    const aiResult = await generateAIHelper(selectedText, helperText);

                    if (aiResult.success) {
                        editor.chain().focus().insertContent(aiResult.text).run();
                    }
                })();

                return true;
            },
        };
    },
});


// Custom TableCell that supports full style + background color
export const CustomTableCell = TableCell.extend({
    addAttributes() {
        return {
            ...this.parent?.(),

            // Enable full inline styles (width, min-width, align, etc.)
            style: {
                default: null,
                parseHTML: element => element.getAttribute('style'),
                renderHTML: attributes => {
                    if (!attributes.style) return {}
                    return { style: attributes.style }
                },
            },

            // Keep your existing backgroundColor logic
            backgroundColor: {
                default: null,
                parseHTML: element =>
                    element.getAttribute('data-background-color') ||
                    element.style.backgroundColor,
                renderHTML: attributes => {
                    if (!attributes.backgroundColor) return {}

                    return {
                        'data-background-color': attributes.backgroundColor,
                        style: `background-color: ${attributes.backgroundColor}`,
                    }
                },
            },
        }
    },
})

export const CustomTable = Table.extend({
    addAttributes() {
        return {
            ...this.parent?.(),

            style: {
                default: "min-width:100%; width:100%; border-collapse:collapse;",
                parseHTML: element => element.getAttribute("style"),
                renderHTML: attrs => ({
                    style: attrs.style,
                }),
            },
        }
    },
})
